// @atlas/contracts — a `profile` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { isoDateStr, MAX_CHILDREN, PersonChildSchema } from '../_shared/schemas.js';
/** A saját profil teljes olvasott alakja (Odin `GET /iam/me/profile`). */
export const ProfileSchema = z.object({
    email: z.string(),
    displayName: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    nickname: z.string().nullable(),
    phone: z.string().nullable(),
    discordId: z.string().nullable(),
    locale: z.string().nullable(),
    jobTitle: z.string().nullable(),
    /** A saját avatar friss presigned GET-URL-je (objektum-tár), ha van feltöltött profilkép — különben null (monogram). */
    avatarUrl: z.string().nullable(),
    notifyEmail: z.boolean(),
    notifyInApp: z.boolean(),
    /** Van-e beállított Asana PAT (write-only titok — a plaintext SOHA nem jön vissza). */
    asanaPatSet: z.boolean(),
    mfaEnabled: z.boolean(),
    /** Van-e folyamatban lévő (még nem aktivált) 2FA-beállítás. */
    twoFactorPending: z.boolean(),
    /** OCC: az identity-MAG verziója — e-mail-változáshoz kötelező (az e-mail a magban él). */
    identityVersion: z.number(),
    /** OCC: a profil-RÉSZ verziója. */
    profileVersion: z.number(),
});
/**
 * A saját profil frissítése. Az `email` VÁLTOZÁSA step-up: `currentPassword` KÖTELEZŐ (különben 403) +
 * `meta.identityVersion` OCC (a mag verziója). A `meta` mindig kötelező. A `currentPassword` write-only.
 */
export const ProfileUpdateBodySchema = z.object({
    givenName: z.string().optional(),
    familyName: z.string().optional(),
    email: z.string().email().optional(),
    // a becenév felső korlátja az Odiné (`MAX_NICKNAME`) — itt is, hogy a hiba már a BFF falán jöjjön
    nickname: z.string().max(120).optional(),
    phone: z.string().optional(),
    discordId: z.string().optional(),
    locale: z.string().optional(),
    notifyEmail: z.boolean().optional(),
    notifyInApp: z.boolean().optional(),
    /** Step-up jelszó — CSAK e-mail-váltáshoz kötelező; write-only (SOHA nem logolt/tárolt). */
    currentPassword: z.string().optional(),
    meta: z.object({ identityVersion: z.number(), profileVersion: z.number() }),
});
/** Asana PAT beállítás — write-only bemenet (a plaintext sosem jön vissza). */
/**
 * A dolgozó SAJÁT személyes adatai a belső Profilomon („Egyéb adatok"): születésnap, névnap, gyerekek. CSAK belső
 * munkatársnak létezik — a partner-portál Profilomán nincs, és a szerver is elutasítja. A belépés és a kilépés
 * napja SZÁNDÉKOSAN nincs itt: azokat a HR a munkatárs-adatlapon kezeli.
 */
export const ProfilePersonalSchema = z.object({
    birthDate: z.string().nullable(),
    nameDay: z.string().nullable(),
    children: z.array(PersonChildSchema),
    /**
     * A próbaidő lejárata (`YYYY-MM-DD`, #111) — CSAK OLVASHATÓ HR-adat (az írás-body nem fogadja). A BFF csak akkor
     * adja, ha a próbaidő be van kapcsolva, és a lejárat napja még nem múlt el (`Europe/Budapest`); egyébként `null`.
     */
    probationEndDate: z.string().nullable().default(null),
    /**
     * Kimaradás a kezdőlap „Ünnepeltek" csempéjéről: `true` esetén a munkatárs SEMMILYEN eseménye (születésnap, névnap,
     * évforduló, új belépő) nem jelenik meg másoknak. CSAK a belső Profilomon létezik (a partner-profilon nincs).
     */
    celebrationsOptOut: z.boolean().default(false),
    /** A HR-sor OCC-verziója — a mentés ezt küldi vissza. */
    version: z.number().int(),
});
/** A személyes adatok írása — mindhárom mező opcionális (parciális), a `version` (OCC) kötelező; más kulcs 422. */
export const ProfilePersonalUpdateBodySchema = z
    .object({
    birthDate: isoDateStr.nullable().optional(),
    nameDay: isoDateStr.nullable().optional(),
    children: z.array(PersonChildSchema).max(MAX_CHILDREN).optional(),
    celebrationsOptOut: z.boolean().optional(),
    version: z.number().int().min(0),
})
    // ismeretlen kulcs (pl. a belépés / kilépés napja, a beosztás) → 422, nem néma eldobás
    .strict();
export const AsanaPatBodySchema = z.object({ asanaPat: z.string().min(1) });
/** Az Asana PAT jelenléti állapota (set/clear válasza). */
export const AsanaPatStateSchema = z.object({ asanaPatSet: z.boolean() });
/** Jelszó-csere — mindkét mező write-only. A policyt (min. 12 kar., kis+nagy+szám) az Odin érvényesíti. */
export const PasswordChangeBodySchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(1),
});
/**
 * Jelszó-policy sértés (422): GÉPI kódok a UI lokalizált üzeneteihez — a nyers upstream-szöveg NEM szivárog.
 * A `path` a sértett szabály helye (pl. `newPassword`), a `code` a SZERVER policy-kódja (pl. `too_short`, `missing_uppercase`).
 */
export const PasswordPolicyErrorSchema = z.object({
    message: z.string(),
    violations: z.array(z.object({ path: z.string(), code: z.string() })),
});
/** 2FA step-up: TOTP/recovery `code` VAGY jelenlegi jelszó — a disable/recovery-codes egyiket kéri. Write-only. */
export const TwoFactorStepUpBodySchema = z.object({
    code: z.string().optional(),
    currentPassword: z.string().optional(),
});
/** 2FA-beállítás indítása → a titkos kulcs + otpauth URI (a UI QR-kódhoz). Egyszeri megjelenítés. */
export const TwoFactorSetupSchema = z.object({ secret: z.string(), otpauthUri: z.string() });
/** 2FA-aktiválás: a hitelesítőből beírt TOTP-kód. */
export const TwoFactorEnableBodySchema = z.object({ code: z.string().min(1) });
/** Recovery-kódok (10 db) — egyszeri megjelenítés; a UI-nak le kell mentetnie a userrel. */
export const RecoveryCodesSchema = z.object({ recoveryCodes: z.array(z.string()) });
/** Engedélyezett avatar-kép MIME-típusok (deny-by-default allowlist; `image/svg+xml` KIZÁRVA — stored-XSS). */
export const AVATAR_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** Presigned PUT-URL kérése: a (kliens-oldalon már négyzetre vágott + downscale-elt) blob típusa + mérete. */
export const AvatarUploadUrlBodySchema = z.object({
    contentType: z.enum(AVATAR_CONTENT_TYPES),
    contentLength: z.number().int().positive(),
});
/** A presigned PUT-URL + a tár-kulcs (a kliens ide PUT-olja a bájtokat). */
export const AvatarUploadUrlSchema = z.object({ uploadUrl: z.string(), key: z.string() });
/** A commit válasza: a friss presigned GET-URL, amit a UI azonnal `<img src>`-be tehet. */
export const AvatarCommitSchema = z.object({ avatarUrl: z.string() });
//# sourceMappingURL=schemas.js.map